import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { RootState } from "../../store";
import * as authSelectors from "../auth/authSelectors";
import * as userTermSelectors from "./userTermSelectors";
import * as userTermActions from "./userTermActions";
import { PageHeader } from "../common/PageHeader";
import { useForm } from "react-hook-form";
import { InputFormField } from "../common/hook-form/InputFormField";
import { routes } from "../../appRoutingHelpers";

interface Props {}

export const UserTermUpsert: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const id = useParams<{ id: string }>().id;
  const isEdit = !!id;
  const navigate = useNavigate();

  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const userTerm = useSelector((state: RootState) =>
    userTermSelectors.userTerm(state, organizationKeyName, id!)
  );

  const isLoading = !userTerm;

  React.useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(userTermActions.getUserTermAction(organizationKeyName, id));
  }, [dispatch, organizationKeyName, id]);

  const formMethods = useForm();
  const { handleSubmit } = formMethods;

  const onSubmit = (data: any) => {
    if (id) {
      data.id = id;
    }

    dispatch(userTermActions.upsertUserTermAction(organizationKeyName, data));
    navigate(routes.userTermList(organizationKeyName));
  };

  const isUpserting = false;

  if (isEdit && isLoading) {
    return null;
  }

  return (
    <div className="container">
      <PageHeader
        title={isEdit ? "Edit User Term" : "Add User Term"}
        breadcrumbs={[
          { title: "User Term list" },
          isEdit
            ? { title: "Edit", isActive: true }
            : { title: "New", isActive: true },
        ]}
      />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                {isEdit ? "Edit User Term" : "Add User Term"}
              </h3>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="card-body">
                <InputFormField
                  name="title"
                  label="Title"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.title}
                />
                <InputFormField
                  name="keyName"
                  label="Key name"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.keyName}
                />
                <InputFormField
                  name="description"
                  label="Description"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.description}
                />
                <InputFormField
                  name="disclaimer"
                  label="Disclaimer"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.disclaimer}
                />
                <InputFormField
                  name="minRequiredVersion"
                  label="Min required version"
                  type="number"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.minRequiredVersion}
                />
                <InputFormField
                  name="emailTemplateName"
                  label="Email template name"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.emailTemplateName}
                />
                <InputFormField
                  name="emailTemplateLocale"
                  label="Email template locale"
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  defaultValue={userTerm?.emailTemplateLocale}
                />
              </div>
              <div className="card-footer text-right">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isUpserting}
                >
                  {isUpserting ? <>Is saving</> : <>Save</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(UserTermUpsert);
